import { useCallback } from 'react';
import cn from 'bem-cn';

import { ScreenSize, useGetScreenSize, useMetrika } from '../../hooks';
import { Button, Icon, ImageWithFadeIn, Link, Stack } from '../UI';

import { useBannerVisibility } from './Banner.hooks';
import { BannerProps } from './Banner.types';
import './Banner.scss';

const b = cn('banner');

export const Banner = ({ storageKey, text, buttonText, url, onClick, metrikaGoal, images }: BannerProps) => {
    const { isVisible, handleClose } = useBannerVisibility(storageKey);
    const { reachGoal } = useMetrika();
    const screenSize = useGetScreenSize();

    const isMobile = screenSize < ScreenSize.Tablet;
    const leftImage = isMobile ? images?.mobileLeft : images?.desktopLeft;
    const rightImage = isMobile ? images?.mobileRight : images?.desktopRight;

    const handleClick = useCallback(() => {
        if (metrikaGoal) {
            reachGoal(metrikaGoal);
        }

        onClick?.();
    }, [metrikaGoal, reachGoal, onClick]);

    if (!isVisible) {
        return null;
    }

    const content = (
        <Stack direction="row" alignItems="center" className={b()}>
            {leftImage && <ImageWithFadeIn className={b('left-image')} src={leftImage} alt="" />}
            <Stack
                direction="row"
                spacing={24}
                alignItems="center"
                justifyContent="center"
                className={b('text-container')}
            >
                <p className={b('text')}>{text}</p>
                {buttonText && <Button className={b('open-button')}>{buttonText}</Button>}
            </Stack>
            <Stack alignItems="center" justifyContent="center" className={b('close-button')}>
                <Icon name="close" size={16} onClick={handleClose} />
            </Stack>
            {rightImage && <ImageWithFadeIn className={b('right-image')} src={rightImage} alt="" />}
        </Stack>
    );

    if (url) {
        return (
            <Link external to={url} onClick={handleClick}>
                {content}
            </Link>
        );
    }

    if (onClick) {
        return (
            <div role="button" tabIndex={0} onClick={handleClick}>
                {content}
            </div>
        );
    }

    return content;
};
